/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FaAd,
  FaCogs,
  FaHome,
  FaIdCard,
  FaMailBulk,
  FaServer,
  FaStoreAlt,
  FaUserAlt,
} from 'react-icons/fa';
import { HiDocumentReport } from 'react-icons/hi';

import React, { PropsWithChildren } from 'react';
import { Outlet } from 'react-router-dom';
import { StackProps, useDisclosure, VStack, Image } from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  FaAddressBook,
  FaArrowsRotate,
  FaBook,
  FaChartBar,
  FaCubes,
  FaPhotoFilm,
  FaSitemap,
  FaSliders,
  FaStore,
  FaTicketSimple,
  FaUsers,
  FaUserTie,
  FaChartPie,
  FaCommentDots,
  FaEnvelope,
} from 'react-icons/fa6';
import { ConfigurationsModal } from '../organisms/ConfigurationsModal.tsx';
import { ProductsProvider, useModules } from '../../contexts/ProductsProvider.tsx';
import { AgreementModules } from '@partiu/api/dashboard/modules.ts';
import { IUser } from '@partiu/api/types';
import { useNavigate } from 'react-router-dom';
import { TopMenuDash, HEADER_HEIGHT } from '@partiu/ui/components/organisms/TopMenuDash.tsx';
import { SIDEBAR_EXPANDED_WIDTH, SIDEBAR_RAIL_WIDTH } from '@partiu/ui/components/organisms/DrawerMenu.tsx';
import { useProfile } from '@partiu/api/user/hooks.ts';
import { useTiledeskDashboardAccess } from '@partiu/api/tiledesk/hooks.ts';
import { MEDIA_MANAGER_HIDDEN_AGREEMENTS } from './RequireModule.tsx';

// Permission check utility
function userHasPermission(user: IUser, permission: string) {
  return user?.agreementSuperuser || user?.agreementStaffPermissions.includes(permission);
}

// Menu configuration with grouped sections
const getMenuLinks = (
  modules: AgreementModules,
  agreementExtraData: Record<string, unknown>,
  agreementId: number | null,
  hasSupportChat: boolean,
) => [
  // Home button as a standalone item
  {
    display: 'Home',
    icon: FaHome,
    path: '/dashboard',
    isExternal: false,
  },

  // Reports section — every report-style page lives here, regardless of the module it measures
  {
    section: 'Relatórios',
    icon: HiDocumentReport,
    items: [
      {
        display: 'Relatórios',
        icon: HiDocumentReport,
        path: '/relatorios',
        showOnQuickMenu: true,
      },
      {
        display: 'Gerenciador de Mídias',
        icon: FaPhotoFilm,
        path: '/relatorios/livros',
        showOnQuickMenu: false,
        canShow: (user: IUser) =>
          !!(
            userHasPermission(user, 'books_report') &&
            modules.hasMedia &&
            !(agreementId && MEDIA_MANAGER_HIDDEN_AGREEMENTS.includes(agreementId))
          ),
      },
      {
        display: 'Utilizações',
        icon: FaChartBar,
        path: '/utilizacoes',
        canShow: (user: IUser) => !!(userHasPermission(user, 'utilization') && modules.hasClub),
      },
      {
        display: 'Relatório de Promocodes',
        icon: FaTicketSimple,
        path: '/promocodes/usage',
        canShow: (user: IUser) => !!(userHasPermission(user, 'promocode') && modules.promocodeModule),
      },
      {
        display: 'Logs de Login e Termos',
        icon: FaUsers,
        path: '/logins',
        canShow: (user: IUser) => userHasPermission(user, 'userbase'),
      },
/*       {
        display: 'Clientes Sincronizados',
        icon: FaArrowsRotate,
        path: '/super-app/clientes-sincronizados',
        canShow: (user: IUser) => !!(userHasPermission(user, 'map_plans') && modules.hasSuperApp),
      }, */
    ]
  },

  ...(agreementExtraData?.financialDashboard
    ? [
        {
          section: 'Financeiro',
          icon: FaChartPie,
          items: [
            {
              display: 'Clientes por categoria',
              icon: FaUsers,
              path: '/financeiro/clientes-por-categoria',
              isExternal: false,
            },
            {
              display: 'Planos por categoria',
              icon: FaSitemap,
              path: '/financeiro/planos-por-categoria',
              isExternal: false,
            },
            {
              display: 'Volumetria',
              icon: FaChartBar,
              path: '/financeiro/volumetria',
              isExternal: false,
            },
            {
              display: 'E-mails enviados',
              icon: FaEnvelope,
              path: '/financeiro/emails-enviados',
              isExternal: false,
            },
          ],
        },
      ]
    : []),

  // User management section
  {
    section: 'Gestão de Usuários',
    icon: FaUsers,
    items: [
      {
        display: 'Subscrições',
        icon: FaIdCard,
        path: '/subscricoes',
        // cpfModule vem do produto Super App; externalCodeSubscriptionModule não tem
        // chave equivalente lá e por isso continua vindo do agreement.
        canShow: (user: IUser) =>
          !!(
            userHasPermission(user, 'vinculation') &&
            (modules.cpfModule || agreementExtraData?.externalCodeSubscriptionModule)
          ),
      },
      {
        // Exceção deliberada: o metadata do produto Super App não tem chave de
        // indicação, então essa flag continua vindo do agreement (extraData),
        // e não dos produtos como as demais entradas deste menu.
        display: 'Indicações',
        icon: FaUserAlt,
        path: '/indicacoes',
        canShow: (user: IUser) => !!(userHasPermission(user, 'referral') && agreementExtraData?.referralModule),
      },
      {
        display: 'Funcionários',
        icon: FaUserTie,
        path: '/funcionarios',
        canShow: (user: IUser) => user?.agreementSuperuser,
        showOnQuickMenu: true,
      },
      {
        display: 'Central de Cliente',
        icon: FaAddressBook,
        path: '/central-de-clientes',
        canShow: () => modules.customerCentralModule,
      },
    ]
  },

  // Promotions section (management only — reporting moved to Relatórios)
  {
    section: 'Promoções',
    icon: FaTicketSimple,
    items: [
      {
        display: 'Promocodes',
        icon: FaTicketSimple,
        path: '/promocodes',
        canShow: (user: IUser) => !!(userHasPermission(user, 'promocode') && modules.promocodeModule),
      },
    ]
  },

  // Communication section — everything the agreement uses to talk to its users
  {
    section: 'Comunicação',
    icon: FaMailBulk,
    items: [
      {
        display: 'Banners',
        icon: FaAd,
        path: '/banners',
        canShow: (user: IUser) => userHasPermission(user, 'banner'),
      },
      {
        display: 'Central de Notificações',
        icon: FaMailBulk,
        path: '/central-de-notificacoes',
        canShow: (user: IUser) => !!(userHasPermission(user, 'push_notification') && modules.pushNotificationModule),
      },
    ]
  },

  // Discounts section — partner catalog the end user browses
  {
    section: 'Descontos',
    icon: FaStore,
    items: [
      {
        display: 'Lojas Parceiras',
        icon: FaStore,
        path: '/lojas',
        canShow: (user: IUser) =>
          !!(userHasPermission(user, 'partner-stores') && modules.hasClub && modules.localModule),
      },
      {
        display: 'Lojas Parceiras Online',
        icon: FaStoreAlt,
        path: '/lojas-online',
        canShow: (user: IUser) =>
          !!(userHasPermission(user, 'partner-stores') && modules.hasClub && modules.ecommerceModule),
      },
      {
        display: 'Revistas Digitais',
        icon: FaBook,
        path: '/revistas-digitais',
        canShow: (user: IUser) => !!(user?.agreementSuperuser && modules.digitalMagazinesModule),
      },
    ]
  },

  // Settings section — Super App setup lives here
  {
    section: 'Configurações',
    icon: FaSliders,
    items: [
      {
        display: 'Mapeamento de Planos',
        icon: FaSitemap,
        path: '/super-app/mapeamento-planos',
        canShow: (user: IUser) => !!(userHasPermission(user, 'map_plans') && modules.hasSuperApp),
      },
      {
        display: 'Módulos Super App',
        icon: FaCubes,
        path: '/super-app/modulos',
        canShow: (user: IUser) => !!(userHasPermission(user, 'map_plans') && modules.hasSuperApp),
      },
    ],
  },

  // Chat de suporte: painel do Tiledesk embutido (iframe com autologin). Só
  // aparece quando o chat do convênio está ativo no admin.
  ...(hasSupportChat
    ? [
        {
          display: 'Chat de Suporte',
          icon: FaCommentDots,
          path: '/chat-de-suporte',
        },
      ]
    : []),

  // Standalone external link — intentionally outside any section
  {
    display: 'Integrações',
    icon: FaServer,
    path: 'https://developer.partiu.com.br/',
    isExternal: true,
  },
];

/**
 * True while rendering inside <DashboardLayout>, which already owns the header and
 * the sidebar. Pages keep using <DashboardTemplate>, but it degrades to a plain
 * content wrapper so the shell is never remounted on navigation.
 */
const DashboardShellContext = React.createContext(false);

export function DashboardShell({ children }: PropsWithChildren) {
  const { id: agreementId, logo, isSidebarCompact, extraData } = useTheme();
  const { data: profile } = useProfile();
  const modules = useModules();
  const { data: supportChat } = useTiledeskDashboardAccess();
  const hasSupportChat = !!supportChat?.url;

  const configurationsModalDisclosure = useDisclosure();
  const navigate = useNavigate();

  const menuSections = React.useMemo(() => {
    const sections = getMenuLinks(modules, extraData, agreementId, hasSupportChat);

    return sections.map(section => {
      if (section.items) {
        const filteredItems = section.items.filter((item: any) => {
          if (typeof item.canShow === 'function' && profile) {
            return item.canShow(profile);
          }
          return true;
        });
        return filteredItems.length > 0 ? { ...section, items: filteredItems } : null;
      } else {
        if ('canShow' in section && typeof section.canShow === 'function' && profile) {
          return section.canShow(profile) ? section : null;
        }
        return section;
      }
    }).filter((section): section is Exclude<typeof section, null> => section !== null) as any[];
  }, [modules, profile, extraData, agreementId, hasSupportChat]);

  const menuLinks = React.useMemo(() => {
    return menuSections.flatMap(section => section.items ? section.items : [section]) as any[];
  }, [menuSections]);

  return (
    <VStack
      paddingTop={{ base: `calc(${HEADER_HEIGHT} + 12px)`, md: `calc(${HEADER_HEIGHT} + 0px)` }}
      paddingLeft={{ base: 0, md: isSidebarCompact ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH }}
      transition="padding-left 0.2s ease-in-out"
      width={'100%'}
    >
      <ConfigurationsModal {...configurationsModalDisclosure} />
      <TopMenuDash
        links={menuLinks}
        drawerLinks={menuSections}
        title={logo ? (
          <Image src={logo} alt="Logo do convênio" maxH="36px" objectFit="contain" />
        ) : undefined}
        quickActions={[
          { icon: FaHome, label: 'Home', path: '/dashboard', onClick: () => navigate('/dashboard') },
          { icon: FaCogs, label: 'Configurações', onClick: () => configurationsModalDisclosure.onOpen() },
        ]}
      />
      <DashboardShellContext.Provider value={true}>{children}</DashboardShellContext.Provider>
    </VStack>
  );
}

/**
 * Route-level layout: renders the shell once and swaps only the page below it.
 * Nest the dashboard routes under a <Route element={<DashboardLayout />}>.
 */
export function DashboardLayout() {
  return (
    <ProductsProvider>
      <DashboardShell>
        <Outlet />
      </DashboardShell>
    </ProductsProvider>
  );
}

export function DashboardTemplate({ children, ...props }: PropsWithChildren & StackProps) {
  const isInsideShell = React.useContext(DashboardShellContext);

  // Standalone usage (page rendered outside the layout route) still needs the shell,
  // which in turn needs its own ProductsProvider ancestor (useModules() has no
  // provider here otherwise, and would silently fall back to all-false modules).
  if (!isInsideShell) {
    return (
      <ProductsProvider>
        <DashboardShell>
          <VStack width={'100%'} {...props}>
            {children}
          </VStack>
        </DashboardShell>
      </ProductsProvider>
    );
  }

  return (
    <VStack width={'100%'} {...props}>
      {children}
    </VStack>
  );
}
